import { API, Endpoints, Lang, LangObject, Tag, TagsAPI } from "@brg/api";
import { ExternalLink, FlexBox, Skeleton, useToasts } from "@brg/ui";
import { useModal } from "core";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DigestLink, TagButtonsWrapper, TagEditButton } from "shared/components/tagAdminProjectsSearchBar/styles";
import { DescriptionContainer } from "./styles";
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { AppTester, getCurrentContextName, getCurrentAppName } from "supporting";
import { HsePolicies } from "shared/constants/HsePolicies";
import { useSelector } from "react-redux";
import { TagPageSelector } from "shared/reducers/TagPageSlice";
import { tagPageCategories } from "./TagPageSwitcher";
import CustomShowMore from "./CustomShowMore";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { useTheme } from "styled-components";

interface ContextDesc {
  appName: string;
  context: string;
  description: LangObject[];
}

interface CanonicalInfo {
  isCanonical: boolean;
  contextInfos: Array<{
    appName: string;
    context: string;
    canonicalType: number;
  }>;
}

interface Description {
  descriptionType: number;
  contexts: Array<{
    appName: string;
    context: string;
  }>;
  position: number;
  description: LangObject[];
  dateCreate: string;
  dateUpdate: string;
}

interface UpdateCanonicalPayload {
  tagId: string;
  canonicalInfo: CanonicalInfo;
  descriptions: Description[];
}

interface TagWithCanonicalDomains extends Tag<'Global'> {
  canonicalDomains?: string[];
  contextDescriptions?: ContextDesc[];
  descriptions?: Description[];
}

interface TagDescriptionComponentProps {
  tagId: string;
  refresh: number;
  onSaved?: () => void;
}

const TAGS_WITH_DIGEST = [
  '2687fadaf0e048ed9925e56052f3010e',
  '269d0812d0d84d4ca64faaea4f3b88cc',
  '3ff5db4e9ed74d91869a35b36826fce3',
  '47ae888ae5fe4343ad29999d04829b44',
  '5164b991a9d8469389175ceeb86d65b8',
  '52644f0d0d9d4a2ea47192e70310ec12',
  '55cc44daca804b979d067f9a59c56e00',
  '80fa55ae7d414e2eb90a170b62c1cce2',
  '8972f434446b43c49dd0db38f5e8e376',
  '9042e2d03e4e490b8c7effb0d2334cc4',
  'ba14bd8702064ea48c3fddf572e225c3',
  'c47bb366fa9c43d1b4a2e0c7f9c62365',
  'dddca0c7066a492a9ea553aca554ab7c',
  'ffa06d1b16b54abba2bbf6c8fb7eaf6d',
];

export const TagDescriptionComponent: FC<TagDescriptionComponentProps> = ({
  tagId,
  refresh,
  onSaved
}) => {
  const { t, i18n: { language } } = useTranslation();
  const profile = useProfile();
  const [time, setTime] = useState(Date.now());
  const modal = useModal();
  const toasts = useToasts();
  const [tag, setTag] = useState<TagWithCanonicalDomains>();
  const [ctxDescriptions, setCtxDescriptions] = useState<ContextDesc[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentCategory } = useSelector(TagPageSelector);
  const theme = useTheme();

  const isCompetitionCategory = useMemo(
    () => currentCategory && ([tagPageCategories.competitions]?.includes(currentCategory)),
    [currentCategory]
  );

  const isGeo = AppTester.isGeograffee();
  const isAcademy = AppTester.isAcademy();
  const isMediiia = AppTester.isMediiia();

  const hasAccessToContentAdmin = profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.access_ui_content_admin)).containsIn(profile.rolesList);

  const tagEditList = useMemo(() => (profile?.rolesList || [])
    .filter(x => x.startsWith(`${HsePolicies.tag_editor_prefix}`))
    .map(x => x.replace(`${HsePolicies.tag_editor_prefix}`, ''))
    .map(x => x.split('_')?.[0] || '')
    .filter(x => !!x),
    [profile]
  );

  const isEditor = useMemo(() => {
    if (!tagEditList?.length) return;
    return tagEditList?.includes(tagId);
  }, [tagId, tagEditList]);

  const canEditDescription = useMemo(
    () => (hasAccessToContentAdmin || isEditor || bProfile.isModer(profile)),
    [hasAccessToContentAdmin, isEditor, profile]
  );

  const shouldDisplayDigestButton = useMemo(() => {
    return isMediiia && TAGS_WITH_DIGEST.includes(tagId);
  }, [tagId]);

  const shouldVisibleDescription = useMemo(() => {
    if (tag?.descriptions && tag.descriptions.length > 0) {
      const firstDescription = tag.descriptions[0];
      return !!firstDescription.description?.length && firstDescription.description?.some(({ name }) => name !== '');
    }
    return !!tag?.description?.length && tag?.description?.some(({ name }) => name !== '');
  }, [tag]);

  const { currentPlatform } = useSelector(TagAdminSelector);

  const hasDescription = useMemo(() => {
    if (tag?.descriptions && tag.descriptions.length > 0) {
      const firstDescription = tag.descriptions[0];
      return firstDescription.description?.some(({ name }) => name !== '');
    }
    return tag?.description?.some(({ name }) => name !== '');
  }, [tag]);

  useEffect(() => {
    if (!tagId) return;

    setLoading(true);
    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url("/api/Tags/GetTag")
      .addQuery("idTag", tagId)
      .addQuery('isIgnoreCanonical', 'true')
      .get()
      .send()
      .then(r => r.json())
      .then((data: TagWithCanonicalDomains) => {
        setTag(data);

        const contextDescs = data.contextDescriptions?.some(cd => cd.context?.trim())
          ? data.contextDescriptions
          : data.descriptions?.filter(d => d.description?.length).map(d => ({
            appName: d.descriptionType === 0 ? 'general' : d.contexts?.[0]?.appName || 'unknown',
            context: d.descriptionType === 0 ? 'general' : d.contexts?.map(ctx => ctx.context).join(',') || 'unknown',
            description: d.description
          })) || [];

        setCtxDescriptions(contextDescs);
      })
      .finally(() => setLoading(false));
  }, [tagId, refresh]);

  const onDescriptionEdit = useCallback(() => {
    if (!currentCategory) return;

    const currentAppName = getCurrentAppName()?.toLowerCase();
    const wantedContext = currentCategory.toLowerCase();

    const generalDescription = ctxDescriptions.find(ctx =>
      ctx.appName === 'general' && ctx.context?.split(',').map(c => c.trim()).includes('general')
    );

    const contextDescription = ctxDescriptions.find(cd =>
      cd.context?.toLowerCase() === wantedContext &&
      cd.appName?.toLowerCase() === currentAppName &&
      !(cd.appName === 'general' && cd.context?.split(',').map(c => c.trim()).includes('general'))
    );

    const mainDescription = generalDescription?.description || tag?.description || [];
    const additionalDescription = contextDescription?.description || [];

    modal.show("tag-description-edit", {
      name: Lang(tag?.name, language),
      description: mainDescription,
      secondName: t("teams.adminPage.fields.additional_description"),
      secondDescription: additionalDescription,
      tagId: tagId,
      canonicalDomains: tag?.canonicalDomains || [],
      onSubmit: onUpdateTag
    });
  }, [tag, ctxDescriptions, currentCategory, language, modal, t, tagId]);

  const onUpdateTag = (payload: UpdateCanonicalPayload) => {
    if (!tag || !currentCategory) return;

    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url("/api/Tags/UpdateCanonical")
      .post()
      .body(payload)
      .send()
      .then(r => {
        if (r?.status !== 200) {
          throw new Error("Не удалось обновить описание тега");
        }
        toasts.info(t('toasts.successfully_updated'));
        modal.hide();
        setTime(Date.now());
        onSaved?.();
      })
      .catch(err => {
        toasts.error(err.message || "Ошибка при сохранении описания");
      });
  };

  const DescriptionLayout = useMemo(() => {
    const wantedContext = getCurrentContextName()?.toLowerCase();
    const wantedAppName = getCurrentAppName()?.toLowerCase();

    const contextDesc = ctxDescriptions.find(cd =>
      cd.context?.trim() &&
      cd.context?.toLowerCase().split(',').map(c => c.trim()).includes(wantedContext)
    ) ||
      ctxDescriptions.find(cd => cd.context?.split(',')?.includes('general'));
    const text = contextDesc?.description?.length ? Lang(contextDesc.description, language) : '';

    if (!shouldVisibleDescription && !canEditDescription && !shouldDisplayDigestButton) {
      return null;
    }

    if (loading) {
      return (
        <FlexBox direction="column" offsetTop={22} offsetTopMobile={22} offsetBottom={32} offsetBottomMobile={24}>
          <Skeleton width={100} />
          <Skeleton width={100} />
        </FlexBox>
      );
    }

    return (
      <DescriptionContainer importantStyles={isCompetitionCategory}>
        {shouldVisibleDescription && (
          <FlexBox
            style={{ maxWidth: 858 }}
            offsetTop={32}
            offsetTopMobile={24}
            offsetBottom={16}
            offsetBottomMobile={16}
          >
            <CustomShowMore
              text={text}
              maxLines={6}
              isDark={isGeo || isAcademy || isCompetitionCategory}
            />
          </FlexBox>
        )}



        <TagButtonsWrapper>
          {shouldDisplayDigestButton && (
            <DigestLink target="_blank" to={`/feed/tag/${tagId}/1`}>
              <TagEditButton
                offsetTop={shouldVisibleDescription ? 1 : 16}
                offsetTopMobile={shouldVisibleDescription ? 1 : 16}
                offsetBottomMobile={1}
              >
                {t('tag_read_digest')}
              </TagEditButton>
            </DigestLink>
          )}
          {canEditDescription && (
            <TagEditButton
              offsetTop={shouldVisibleDescription ? 1 : 16}
              offsetTopMobile={shouldVisibleDescription ? 1 : 16}
              offsetBottomMobile={1}
              onClick={onDescriptionEdit}
            >
              {t(`tag_description_${hasDescription ? 'edit' : 'add'}`)}
            </TagEditButton>
          )}

        </TagButtonsWrapper>
      </DescriptionContainer>
    );
  }, [
    language,
    tag,
    shouldVisibleDescription,
    shouldDisplayDigestButton,
    loading,
    canEditDescription,
    ctxDescriptions,
    currentCategory,
    theme.text.label,
    isGeo,
    isAcademy,
    isCompetitionCategory,
    hasDescription,
    onDescriptionEdit
  ]);

  return DescriptionLayout;
};import { API, Endpoints, Lang, LangObject, Tag, TagsAPI } from "@brg/api";
import { ExternalLink, FlexBox, Skeleton, useToasts } from "@brg/ui";
import { useModal } from "core";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DigestLink, TagButtonsWrapper, TagEditButton } from "shared/components/tagAdminProjectsSearchBar/styles";
import { DescriptionContainer } from "./styles";
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { AppTester, getCurrentContextName, getCurrentAppName } from "supporting";
import { HsePolicies } from "shared/constants/HsePolicies";
import { useSelector } from "react-redux";
import { TagPageSelector } from "shared/reducers/TagPageSlice";
import { tagPageCategories } from "./TagPageSwitcher";
import CustomShowMore from "./CustomShowMore";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { useTheme } from "styled-components";

interface ContextDesc {
  appName: string;
  context: string;
  description: LangObject[];
}

interface CanonicalInfo {
  isCanonical: boolean;
  contextInfos: Array<{
    appName: string;
    context: string;
    canonicalType: number;
  }>;
}

interface Description {
  descriptionType: number;
  contexts: Array<{
    appName: string;
    context: string;
  }>;
  position: number;
  description: LangObject[];
  dateCreate: string;
  dateUpdate: string;
}

interface UpdateCanonicalPayload {
  tagId: string;
  canonicalInfo: CanonicalInfo;
  descriptions: Description[];
}

interface TagWithCanonicalDomains extends Tag<'Global'> {
  canonicalDomains?: string[];
  contextDescriptions?: ContextDesc[];
  descriptions?: Description[];
}

interface TagDescriptionComponentProps {
  tagId: string;
  refresh: number;
  onSaved?: () => void;
}

const TAGS_WITH_DIGEST = [
  '2687fadaf0e048ed9925e56052f3010e',
  '269d0812d0d84d4ca64faaea4f3b88cc',
  '3ff5db4e9ed74d91869a35b36826fce3',
  '47ae888ae5fe4343ad29999d04829b44',
  '5164b991a9d8469389175ceeb86d65b8',
  '52644f0d0d9d4a2ea47192e70310ec12',
  '55cc44daca804b979d067f9a59c56e00',
  '80fa55ae7d414e2eb90a170b62c1cce2',
  '8972f434446b43c49dd0db38f5e8e376',
  '9042e2d03e4e490b8c7effb0d2334cc4',
  'ba14bd8702064ea48c3fddf572e225c3',
  'c47bb366fa9c43d1b4a2e0c7f9c62365',
  'dddca0c7066a492a9ea553aca554ab7c',
  'ffa06d1b16b54abba2bbf6c8fb7eaf6d',
];

export const TagDescriptionComponent: FC<TagDescriptionComponentProps> = ({
  tagId,
  refresh,
  onSaved
}) => {
  const { t, i18n: { language } } = useTranslation();
  const profile = useProfile();
  const [time, setTime] = useState(Date.now());
  const modal = useModal();
  const toasts = useToasts();
  const [tag, setTag] = useState<TagWithCanonicalDomains>();
  const [ctxDescriptions, setCtxDescriptions] = useState<ContextDesc[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentCategory } = useSelector(TagPageSelector);
  const theme = useTheme();

  const isCompetitionCategory = useMemo(
    () => currentCategory && ([tagPageCategories.competitions]?.includes(currentCategory)),
    [currentCategory]
  );

  const isGeo = AppTester.isGeograffee();
  const isAcademy = AppTester.isAcademy();
  const isMediiia = AppTester.isMediiia();

  const hasAccessToContentAdmin = profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.access_ui_content_admin)).containsIn(profile.rolesList);

  const tagEditList = useMemo(() => (profile?.rolesList || [])
    .filter(x => x.startsWith(`${HsePolicies.tag_editor_prefix}`))
    .map(x => x.replace(`${HsePolicies.tag_editor_prefix}`, ''))
    .map(x => x.split('_')?.[0] || '')
    .filter(x => !!x),
    [profile]
  );

  const isEditor = useMemo(() => {
    if (!tagEditList?.length) return;
    return tagEditList?.includes(tagId);
  }, [tagId, tagEditList]);

  const canEditDescription = useMemo(
    () => (hasAccessToContentAdmin || isEditor || bProfile.isModer(profile)),
    [hasAccessToContentAdmin, isEditor, profile]
  );

  const shouldDisplayDigestButton = useMemo(() => {
    return isMediiia && TAGS_WITH_DIGEST.includes(tagId);
  }, [tagId]);

  const shouldVisibleDescription = useMemo(() => {
    if (tag?.descriptions && tag.descriptions.length > 0) {
      const firstDescription = tag.descriptions[0];
      return !!firstDescription.description?.length && firstDescription.description?.some(({ name }) => name !== '');
    }
    return !!tag?.description?.length && tag?.description?.some(({ name }) => name !== '');
  }, [tag]);

  const { currentPlatform } = useSelector(TagAdminSelector);

  const hasDescription = useMemo(() => {
    if (tag?.descriptions && tag.descriptions.length > 0) {
      const firstDescription = tag.descriptions[0];
      return firstDescription.description?.some(({ name }) => name !== '');
    }
    return tag?.description?.some(({ name }) => name !== '');
  }, [tag]);

  useEffect(() => {
    if (!tagId) return;

    setLoading(true);
    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url("/api/Tags/GetTag")
      .addQuery("idTag", tagId)
      .addQuery('isIgnoreCanonical', 'true')
      .get()
      .send()
      .then(r => r.json())
      .then((data: TagWithCanonicalDomains) => {
        setTag(data);

        const contextDescs = data.contextDescriptions?.some(cd => cd.context?.trim())
          ? data.contextDescriptions
          : data.descriptions?.filter(d => d.description?.length).map(d => ({
            appName: d.descriptionType === 0 ? 'general' : d.contexts?.[0]?.appName || 'unknown',
            context: d.descriptionType === 0 ? 'general' : d.contexts?.map(ctx => ctx.context).join(',') || 'unknown',
            description: d.description
          })) || [];

        setCtxDescriptions(contextDescs);
      })
      .finally(() => setLoading(false));
  }, [tagId, refresh]);

  const onDescriptionEdit = useCallback(() => {
    if (!currentCategory) return;

    const currentAppName = getCurrentAppName()?.toLowerCase();
    const wantedContext = currentCategory.toLowerCase();

    const generalDescription = ctxDescriptions.find(ctx =>
      ctx.appName === 'general' && ctx.context?.split(',').map(c => c.trim()).includes('general')
    );

    const contextDescription = ctxDescriptions.find(cd =>
      cd.context?.toLowerCase() === wantedContext &&
      cd.appName?.toLowerCase() === currentAppName &&
      !(cd.appName === 'general' && cd.context?.split(',').map(c => c.trim()).includes('general'))
    );

    const mainDescription = generalDescription?.description || tag?.description || [];
    const additionalDescription = contextDescription?.description || [];

    modal.show("tag-description-edit", {
      name: Lang(tag?.name, language),
      description: mainDescription,
      secondName: t("teams.adminPage.fields.additional_description"),
      secondDescription: additionalDescription,
      tagId: tagId,
      canonicalDomains: tag?.canonicalDomains || [],
      onSubmit: onUpdateTag
    });
  }, [tag, ctxDescriptions, currentCategory, language, modal, t, tagId]);

  const onUpdateTag = (payload: UpdateCanonicalPayload) => {
    if (!tag || !currentCategory) return;

    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url("/api/Tags/UpdateCanonical")
      .post()
      .body(payload)
      .send()
      .then(r => {
        if (r?.status !== 200) {
          throw new Error("Не удалось обновить описание тега");
        }
        toasts.info(t('toasts.successfully_updated'));
        modal.hide();
        setTime(Date.now());
        onSaved?.();
      })
      .catch(err => {
        toasts.error(err.message || "Ошибка при сохранении описания");
      });
  };

  const DescriptionLayout = useMemo(() => {
    const wantedContext = getCurrentContextName()?.toLowerCase();
    const wantedAppName = getCurrentAppName()?.toLowerCase();

    const contextDesc = ctxDescriptions.find(cd =>
      cd.context?.trim() &&
      cd.context?.toLowerCase().split(',').map(c => c.trim()).includes(wantedContext)
    ) ||
      ctxDescriptions.find(cd => cd.context?.split(',')?.includes('general'));
    const text = contextDesc?.description?.length ? Lang(contextDesc.description, language) : '';

    if (!shouldVisibleDescription && !canEditDescription && !shouldDisplayDigestButton) {
      return null;
    }

    if (loading) {
      return (
        <FlexBox direction="column" offsetTop={22} offsetTopMobile={22} offsetBottom={32} offsetBottomMobile={24}>
          <Skeleton width={100} />
          <Skeleton width={100} />
        </FlexBox>
      );
    }

    return (
      <DescriptionContainer importantStyles={isCompetitionCategory}>
        {shouldVisibleDescription && (
          <FlexBox
            style={{ maxWidth: 858 }}
            offsetTop={32}
            offsetTopMobile={24}
            offsetBottom={16}
            offsetBottomMobile={16}
          >
            <CustomShowMore
              text={text}
              maxLines={6}
              isDark={isGeo || isAcademy || isCompetitionCategory}
            />
          </FlexBox>
        )}



        <TagButtonsWrapper>
          {shouldDisplayDigestButton && (
            <DigestLink target="_blank" to={`/feed/tag/${tagId}/1`}>
              <TagEditButton
                offsetTop={shouldVisibleDescription ? 1 : 16}
                offsetTopMobile={shouldVisibleDescription ? 1 : 16}
                offsetBottomMobile={1}
              >
                {t('tag_read_digest')}
              </TagEditButton>
            </DigestLink>
          )}
          {canEditDescription && (
            <TagEditButton
              offsetTop={shouldVisibleDescription ? 1 : 16}
              offsetTopMobile={shouldVisibleDescription ? 1 : 16}
              offsetBottomMobile={1}
              onClick={onDescriptionEdit}
            >
              {t(`tag_description_${hasDescription ? 'edit' : 'add'}`)}
            </TagEditButton>
          )}

        </TagButtonsWrapper>
      </DescriptionContainer>
    );
  }, [
    language,
    tag,
    shouldVisibleDescription,
    shouldDisplayDigestButton,
    loading,
    canEditDescription,
    ctxDescriptions,
    currentCategory,
    theme.text.label,
    isGeo,
    isAcademy,
    isCompetitionCategory,
    hasDescription,
    onDescriptionEdit
  ]);

  return DescriptionLayout;
};